import {
	type AggRequest,
	type AggResponse,
	fetchAgg,
} from '../../../../../../common/utils/fetch-agg';
import { generateHashFromVariables } from '../../../../../../utils/caching/common/key-generator';
import { inMemoryCache } from '../../../../../../utils/caching/in-memory';
import {
	pastMeetingOccurrencesCypherQueryGenerator,
	type PastMeetingOccurrencesCypherQueryVariables,
} from './query-generator';
import {
	type PastMeetingCypherQueryData,
	type PastMeetingOccurrence,
	type PastMeetingOccurrencesMap,
} from './types';

/**
 * Cypher query: given a video ARI, walk up to its parent recurring CalendarEvent,
 * then return up to 5 sibling past-occurrence videos ordered by meeting date DESC.
 *
 * Variables: $videoAri — the ARI of the current meeting video.
 */
const TEAMS_PAST_MEETING_OCCURRENCES_CYPHER_QUERY = `
MATCH (video:GraphVideo {ari: $videoAri})
    <-[:external_calendar_event_links_external_video]-(child:GraphCalendarEvent)
    <-[:external_calendar_event_has_child_external_calendar_event]-(parent:GraphCalendarEvent)
MATCH (parent)-[:external_calendar_event_has_child_external_calendar_event]->(sibling:GraphCalendarEvent)
    -[:external_calendar_event_links_external_video]->(siblingVideo:GraphVideo)
WHERE sibling.createdAt < child.createdAt
RETURN siblingVideo
ORDER BY sibling.createdAt DESC
LIMIT 5
`;

const ZOOM_PAST_MEETING_OCCURRENCES_CYPHER_QUERY = `
MATCH (sourceVideo:GraphVideo {ari: $videoAri})<-[:external_calendar_event_links_external_video]-
  (calendarEvent:GraphCalendarEvent)
  -[:external_calendar_event_links_external_video]->
  (video:GraphVideo)
WHERE video.createdAt < sourceVideo.createdAt
RETURN video
ORDER BY video.createdAt DESC
LIMIT 5
`;

export type PastMeetingOccurrencesQueryType = 'teams' | 'zoom';

export type PastMeetingOccurrencesBatchInput = {
	videoAri: string;
	queryType: PastMeetingOccurrencesQueryType;
};

const PAST_MEETING_OCCURRENCES_CYPHER_QUERIES: Record<PastMeetingOccurrencesQueryType, string> = {
	teams: TEAMS_PAST_MEETING_OCCURRENCES_CYPHER_QUERY,
	zoom: ZOOM_PAST_MEETING_OCCURRENCES_CYPHER_QUERY,
};

const cache = inMemoryCache<AggResponse<PastMeetingCypherQueryData>>();

const expireOnErrorOrEmpty = <T>(result: AggResponse<T>) => {
	const { data, errors } = result;
	return !data || (!!errors && errors.length > 0);
};

/**
 * Extracts past meeting occurrences from a cypherQueryV2 response.
 * Each edge's column value contains an ExternalVideo node with url, displayName, createdAt.
 */
function parsePastMeetingOccurrences(
	response: AggResponse<PastMeetingCypherQueryData>,
): PastMeetingOccurrence[] {
	const edges = response.data?.graphStore?.cypherQueryV2?.edges;
	if (!edges || edges.length === 0) {
		return [];
	}

	const occurrences: PastMeetingOccurrence[] = [];
	for (const edge of edges) {
		for (const column of edge.node.columns) {
			const data = column.value?.data;
			if (data?.url) {
				occurrences.push({
					url: data.url,
					title: data.displayName ?? null,
					lastModifiedDate: data.createdAt ?? null,
					durationInSeconds: data.durationInSeconds ?? null,
					thirdPartyId: data.thirdPartyId ?? null,
					providerId: data.provider?.providerId ?? null,
				});
			}
		}
	}
	return occurrences;
}

export type FetchPastMeetingOccurrencesProps = Pick<
	AggRequest<Record<string, unknown>>,
	'aggAbsoluteUrl' | 'headers'
> & {
	queryType: PastMeetingOccurrencesQueryType;
};

type FetchPastMeetingOccurrencesBatchProps = Pick<
	AggRequest<Record<string, unknown>>,
	'aggAbsoluteUrl' | 'headers'
>;

/**
 * Fetches the 5 most recent past occurrences of a recurring meeting video,
 * given the ARI of the current meeting video.
 *
 * Uses an in-memory cache keyed by videoAri, queryContext, and queryType to avoid duplicate fetches.
 *
 * @param videoAri - ARI of the current ExternalVideo (SearchResultGraphDocument.id)
 * @param queryContext - Site ARI for TWG workspace routing (e.g. "ari:cloud:platform::site/<cloudId>")
 * @param fetchProps - AGG request config (aggAbsoluteUrl, headers, query type)
 *
 */
export async function fetchPastMeetingOccurrences(
	videoAri: string,
	queryContext: string,
	{ aggAbsoluteUrl, headers, queryType }: FetchPastMeetingOccurrencesProps,
): Promise<PastMeetingOccurrence[]> {
	const graphQuery = pastMeetingOccurrencesCypherQueryGenerator();
	const cypherQuery = PAST_MEETING_OCCURRENCES_CYPHER_QUERIES[queryType];
	const variables: PastMeetingOccurrencesCypherQueryVariables = {
		cypherQuery,
		params: { videoAri },
		queryContext,
	};

	const response = await cache.inMemoryDecorator(
		generateHashFromVariables({ videoAri, queryContext, queryType }),
		() =>
			fetchAgg<PastMeetingOccurrencesCypherQueryVariables, PastMeetingCypherQueryData>({
				variables,
				aggAbsoluteUrl,
				graphQuery,
				operationName: 'SearchPagePastMeetingOccurrencesHydration',
				headers,
			}),
		expireOnErrorOrEmpty,
	);

	return parsePastMeetingOccurrences(response);
}

/**
 * Fetches past meeting occurrences for multiple video ARIs in parallel.
 *
 * @param inputs - Array of ExternalVideo ARIs and query types to look up
 * @param queryContext - Site ARI for TWG workspace routing (e.g. "ari:cloud:platform::site/<cloudId>")
 * @param fetchProps - AGG request config (aggAbsoluteUrl, headers)
 * @returns Map of videoAri → past meeting occurrences (null if fetch failed for that ARI)
 */
export async function fetchPastMeetingOccurrencesBatch(
	inputs: PastMeetingOccurrencesBatchInput[],
	queryContext: string,
	fetchProps: FetchPastMeetingOccurrencesBatchProps,
): Promise<PastMeetingOccurrencesMap> {
	const results: PastMeetingOccurrencesMap = {};

	const promises = inputs.map(async (input) => {
		try {
			const occurrences = await fetchPastMeetingOccurrences(input.videoAri, queryContext, {
				...fetchProps,
				queryType: input.queryType,
			});
			results[input.videoAri] = occurrences;
		} catch {
			results[input.videoAri] = null;
		}
	});

	await Promise.all(promises);
	return results;
}
